import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ChevronDown } from "lucide-react";
import { content } from "../../data/content";
import { FloatingShapes } from "./FloatingShapes";
import { scrollToId } from "../../hooks/useLenis";

const EASE = [0.34, 1.56, 0.64, 1];

function MaskLine({ children, delay, className }) {
  return (
    <span className="block overflow-hidden pb-[0.12em]">
      <motion.span
        className={`block leading-[1] ${className}`}
        initial={{ y: "120%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full bg-blush flex flex-col items-center justify-center overflow-hidden px-4"
    >
      <FloatingShapes />

      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-10 flex flex-col items-center text-center"
      >
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="font-accent text-2xl md:text-4xl text-rose mb-2 md:mb-4 -rotate-2"
        >
          {content.heroKicker}
        </motion.p>

        <h1 className="font-heading font-black uppercase tracking-tighter leading-[0.95] text-ink">
          <MaskLine delay={0.35} className="text-5xl md:text-[8vw]">
            {content.heroLineTop}
          </MaskLine>
          <MaskLine
            delay={0.5}
            className="text-6xl md:text-[11vw] text-rose"
          >
            {content.recipientFirst}
          </MaskLine>
          <MaskLine delay={0.65} className="text-5xl md:text-[8vw]">
            {content.recipientSecond}
          </MaskLine>
        </h1>

        <motion.button
          data-testid="hero-scroll-btn"
          onClick={() => scrollToId("gift")}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="mt-10 md:mt-14 inline-flex items-center gap-2 rounded-full bg-ink text-white font-body font-bold px-7 py-4 text-base md:text-lg shadow-brutal hover:shadow-brutal-lg transition-shadow"
        >
          Empezar la sorpresa
          <ChevronDown className="w-5 h-5" />
        </motion.button>
      </motion.div>

      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="w-7 h-7 text-ink/60" />
        </motion.div>
      </motion.div>
    </section>
  );
}
